'use client';

/**
 * Purchase Flow Config Provider
 *
 * Single context that holds all configuration for the purchase flow.
 * Wraps the AnalyticsProvider internally so everything is set up in one place.
 *
 * Hooks:
 *   usePurchaseRender()   → { renderIcon, renderText }
 *   usePurchaseTheme()    → { theme, textColor }
 *   usePurchaseLog()      → log function
 *   usePurchaseCaptcha()  → withCaptcha function
 *   usePurchaseAnalytics() → analytics service (from analytics module)
 */

import React, {
  createContext, useContext, useCallback, useEffect, useMemo
} from 'react';
import type { PurchaseFlowConfig, PurchaseFlowTheme, WithCaptchaFn } from '../types/config';
import type { OnLogEvent } from '../types/logger';
import { AnalyticsProvider } from '../analytics/PurchaseFlowAnalytics';
import { setApiRequestHeaders } from '../api/core';

// Re-export for convenience
export type { PurchaseFlowConfig, PurchaseFlowTheme, WithCaptchaFn };

// ============================================================================
// Theme helpers
// ============================================================================

const THEME_TEXT_COLORS: Record<PurchaseFlowTheme, string> = {
  light: '#121212',
  dark: '#EEEEEE'
};

export const getTextColorFromTheme = (theme?: PurchaseFlowTheme): string => THEME_TEXT_COLORS[theme || 'light'];

// ============================================================================
// Context
// ============================================================================

const ConfigContext = createContext<PurchaseFlowConfig | null>(null);

const useConfigContext = (): PurchaseFlowConfig => {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    throw new Error('usePurchaseConfig must be used within PurchaseFlowConfigProvider');
  }
  return ctx;
};

// ============================================================================
// Provider
// ============================================================================

interface ProviderProps {
  config: PurchaseFlowConfig;
  /** Analytics config enriched with price/bonus/isPayg from PurchaseFlow props */
  analyticsConfig: Record<string, unknown>;
  children: React.ReactNode;
}

export const PurchaseFlowConfigProvider: React.FC<ProviderProps> = ({
  config,
  analyticsConfig,
  children
}) => {
  // Register / clear the host-app header factory so apiRequest() can use it
  useEffect(() => {
    setApiRequestHeaders(config.requestHeaders);
    return () => { setApiRequestHeaders(undefined); };
  }, [config.requestHeaders]);

  return (
    <ConfigContext.Provider value={ config }>
      <AnalyticsProvider
        config={ analyticsConfig }
        onAnalyticsEvent={ config.onAnalyticsEvent }
        onTrackPageVisit={ config.onTrackPageVisit }
      >
        {children}
      </AnalyticsProvider>
    </ConfigContext.Provider>
  );
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Returns renderIcon and renderText for UI components.
 */
export const usePurchaseRender = () => {
  const { renderIcon, renderText } = useConfigContext();
  return useMemo(() => ({ renderIcon, renderText }), [renderIcon, renderText]);
};

/**
 * Returns theme and derived text color.
 */
export const usePurchaseTheme = () => {
  const { theme } = useConfigContext();

  return useMemo(() => ({
    theme: theme ?? 'light',
    textColor: getTextColorFromTheme(theme)
  }), [theme]);
};

/**
 * Returns the log function. Calls are no-ops if there is no provider, no `onLog`
 * on config, or when `onLog` is omitted. Safe to call outside `PurchaseFlowConfigProvider`.
 */
export const usePurchaseLog = (): OnLogEvent => {
  const ctx = useContext(ConfigContext);
  return useCallback<OnLogEvent>(
    (level, tag, message, data) => { ctx?.onLog?.(level, tag, message, data); },
    [ctx?.onLog]
  );
};

/**
 * Returns withCaptcha. Throws at call time if not configured.
 */
export const usePurchaseCaptcha = (): WithCaptchaFn => {
  const { withCaptcha } = useConfigContext();

  return useCallback<WithCaptchaFn>(
    (action, callback) => {
      if (!withCaptcha) {
        throw new Error('withCaptcha not configured in PurchaseFlowConfig');
      }
      withCaptcha(action, callback);
    },
    [withCaptcha]
  );
};

/**
 * Returns whether IAP is enabled. Defaults to true when not configured.
 */
export const useIapEnabled = (): boolean => {
  const { iapEnabled } = useConfigContext();
  return iapEnabled !== false;
};

const DEFAULT_MIN_PURCHASE_AMOUNT = 10;

/**
 * Returns the minimum purchase amount for additionalFunds mode.
 * Defaults to $10.00 when not configured.
 */
export const useMinPurchaseAmount = (): number => {
  const { minPurchaseAmount } = useConfigContext();
  return minPurchaseAmount ?? DEFAULT_MIN_PURCHASE_AMOUNT;
};

/**
 * Returns the user's current available credit (balance).
 * Defaults to 0 when not configured.
 */
export const useClientAvailableCredit = (): number => {
  const { clientAvailableCredit } = useConfigContext();
  return clientAvailableCredit ?? 0;
};

/**
 * Returns the current app language code (e.g. 'en', 'es', 'pt').
 * Defaults to 'en' when not configured.
 */
export const usePurchaseLocale = (): string => {
  const ctx = useContext(ConfigContext);
  return ctx?.locale ?? 'en';
};
